import type { SyncManager } from '@wiztime/sync';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { legalPath } from '../../app/routes';
import { toastError, toastInfo } from '../../shell/toastStore';
import { Button, ButtonVariant } from '../../ui/Button';
import {
  AccountFailure,
  AccountFormFrame,
  EmailField,
  isValidEmail,
  PasswordField,
  passwordProblem,
  PasswordProblem,
  useAccountSubmit,
} from './accountForm';
import { RecoverySheet } from './RecoverySheet';
import styles from './Sync.module.css';
import { toConfigUrl } from './urls';

interface RegisterFlowProps {
  readonly manager: SyncManager;
  readonly serverUrl: string;
  readonly onBack: () => void;
  /** Called when the user has saved the recovery code and finishes. */
  readonly onDone: () => void;
  readonly onUnsupported: () => void;
}

/** Creates an account, then shows the recovery code once; the user must confirm having saved it. */
export function RegisterFlow({ manager, serverUrl, onBack, onDone, onUnsupported }: RegisterFlowProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [consented, setConsented] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const submit = useAccountSubmit(onUnsupported);

  if (recoveryCode !== null) {
    return (
      <RecoveryCodeView
        manager={manager}
        recoveryCode={recoveryCode}
        verificationRequired={verificationRequired}
        email={email.trim()}
        onDone={onDone}
      />
    );
  }

  const problem = passwordProblem(password, confirm);
  const emailOk = isValidEmail(email);
  const valid = emailOk && problem === null && acknowledged && consented;

  const register = async (): Promise<void> => {
    const result = await submit.run(() =>
      manager.register({ serverUrl: toConfigUrl(serverUrl), email: email.trim(), password }),
    );
    if (result === undefined) return;
    setRecoveryCode(result.recoveryCode);
    // A server that requires a verified email has sent a mail; tell the user at once instead of at the first refused upload.
    manager.accountInfo().then(
      (info) => setVerificationRequired(info.verificationRequired),
      (error: unknown) => console.debug('[sync] could not read the account state', error),
    );
  };

  return (
    <AccountFormFrame
      title={t('sync.account.register.title')}
      busy={submit.busy}
      testId="account-register"
      onSubmit={() => valid && void register()}
      status={
        <AccountFailure
          failure={submit.failure}
          retryable={submit.retryable}
          onRetry={() => void register()}
        />
      }
      actions={
        <>
          <Button disabled={submit.busy} onClick={onBack} data-testid="account-back">
            {t('sync.back')}
          </Button>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            disabled={!valid || submit.busy}
            data-testid="account-submit"
          >
            {t('sync.account.register.submit')}
          </Button>
        </>
      }
    >
      <EmailField
        label={t('sync.account.field.email')}
        value={email}
        onChange={setEmail}
        testId="account-email"
        invalid={email !== '' && !emailOk}
        message={email !== '' && !emailOk ? t('sync.account.invalid.email') : undefined}
      />
      <PasswordField
        label={t('sync.account.field.password')}
        value={password}
        onChange={setPassword}
        testId="account-password"
        autoComplete="new-password"
        invalid={password !== '' && problem === PasswordProblem.Short}
        message={
          password !== '' && problem === PasswordProblem.Short
            ? t('sync.account.invalid.passwordShort')
            : t('sync.account.hint.password')
        }
      />
      <PasswordField
        label={t('sync.account.field.confirm')}
        value={confirm}
        onChange={setConfirm}
        testId="account-password-confirm"
        autoComplete="new-password"
        invalid={confirm !== '' && password !== confirm}
        message={confirm !== '' && password !== confirm ? t('sync.account.invalid.mismatch') : undefined}
      />
      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
          data-testid="account-ack"
        />
        <span>{t('sync.account.register.ack')}</span>
      </label>
      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={consented}
          onChange={(event) => setConsented(event.target.checked)}
          data-testid="account-consent"
        />
        <span>
          <Trans
            i18nKey="sync.account.register.consent"
            components={{
              privacy: (
                <a
                  href={`#${legalPath('privacy')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="account-consent-privacy"
                />
              ),
              terms: (
                <a
                  href={`#${legalPath('terms')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="account-consent-terms"
                />
              ),
            }}
          />
        </span>
      </label>
    </AccountFormFrame>
  );
}

interface RecoveryCodeViewProps {
  readonly manager: SyncManager;
  readonly recoveryCode: string;
  /** The server asks for a verified email and has mailed a link to `email`. */
  readonly verificationRequired: boolean;
  readonly email: string;
  readonly onDone: () => void;
}

/** The 12-word recovery code, with copy and print; "Done" only after the user confirms having saved it. */
function RecoveryCodeView({ manager, recoveryCode, verificationRequired, email, onDone }: RecoveryCodeViewProps) {
  const { t, i18n } = useTranslation();
  const words = recoveryCode.split(' ');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toastError(t('sync.generate.copyFailed'));
    }
  };

  return (
    <div className={styles.flow} data-testid="account-recovery">
      {verificationRequired ? (
        <div className={styles.notice} role="status" data-testid="register-verify-notice">
          <strong>{t('sync.account.verification.title')}</strong>
          <p>{t('sync.account.verification.registered', { email })}</p>
        </div>
      ) : null}
      <h3 className={styles.flowTitle}>{t('sync.account.recovery.title')}</h3>
      <p className={styles.muted}>{t('sync.account.recovery.intro')}</p>
      <ol className={styles.words} data-testid="recovery-words">
        {words.map((word, index) => (
          <li key={index} data-testid="recovery-word">
            <span className={styles.wordNumber}>{index + 1}</span>
            <span>{word}</span>
          </li>
        ))}
      </ol>
      <div className={styles.actions}>
        <Button onClick={() => void copy()} data-testid="recovery-copy">
          {copied ? t('sync.generate.copied') : t('sync.generate.copy')}
        </Button>
        <Button onClick={() => setPrinting(true)} data-testid="recovery-print">
          {t('sync.generate.print')}
        </Button>
      </div>
      <p className={styles.warning}>{t('sync.account.recovery.warning')}</p>
      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={saved}
          onChange={(event) => setSaved(event.target.checked)}
          data-testid="recovery-saved"
        />
        <span>{t('sync.account.recovery.saved')}</span>
      </label>
      <div className={styles.actions}>
        <Button
          variant={ButtonVariant.Primary}
          disabled={!saved}
          onClick={() => {
            toastInfo(t('sync.account.recovery.registered'));
            onDone();
          }}
          data-testid="recovery-done"
        >
          {t('sync.account.recovery.done')}
        </Button>
      </div>
      {printing ? (
        <RecoverySheet
          words={words}
          spaceIdPrefix={manager.getSettings()?.spaceIdPrefix ?? null}
          dateText={new Date().toLocaleDateString(i18n.language)}
          title={t('sync.account.recovery.sheetTitle')}
          warning={t('sync.account.recovery.warning')}
          onDone={() => setPrinting(false)}
        />
      ) : null}
    </div>
  );
}
